import { range } from 'lodash';
import React, { useMemo, useRef } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import styles from './ProgressSegmentLine.module.css';

interface ProgressSegmentLineProps {
  currentQuestionIndex: number;
  previousQuestionIndex: number;
  questionCount: number;
}

const getProgressSegmentState = ({
  index,
  previousQuestionIndex,
  currentQuestionIndex,
}: {
  index: number;
  previousQuestionIndex: number;
  currentQuestionIndex: number;
}) => {
  if (index === currentQuestionIndex && currentQuestionIndex > previousQuestionIndex) {
    return 'grow';
  }

  if (index === previousQuestionIndex && currentQuestionIndex < previousQuestionIndex) {
    return 'decrease';
  }

  return index > currentQuestionIndex ? 'empty' : 'filled';
};

export const ProgressSegmentLine = ({
  currentQuestionIndex,
  previousQuestionIndex,
  questionCount,
}: ProgressSegmentLineProps) => {
  const totalWithFinal = questionCount + 1;

  return (
    <div className={styles.segmentsContainer}>
      {range(totalWithFinal).map(index => (
        <ProgressSegment
          key={index}
          state={getProgressSegmentState({ index, currentQuestionIndex, previousQuestionIndex })}
        />
      ))}
    </div>
  );
};

const transitionDuration = 250;

interface ProgressSegmentProps {
  state: 'grow' | 'decrease' | 'filled' | 'empty';
}

const ProgressSegment = ({ state }: ProgressSegmentProps) => {
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const { startStyles, transitionStyles } = useMemo(() => {
    const startPercent = state === 'filled' || state === 'decrease' ? 100 : 0;
    const endPercent = state === 'empty' || state === 'decrease' ? 0 : 100;

    return {
      startStyles: {
        width: `${startPercent}%`,
        transition: `width ${transitionDuration}ms linear`,
      } satisfies React.CSSProperties,
      transitionStyles: {
        entering: { width: `${endPercent}%` },
        entered: { width: `${endPercent}%` },
        exiting: {},
        exited: {},
        unmounted: {},
      } satisfies Record<TransitionStatus, React.CSSProperties>,
    };
  }, [state]);

  return (
    <div className={styles.progressSegment}>
      <Transition nodeRef={nodeRef} timeout={transitionDuration} appear in>
        {state => (
          <div
            ref={nodeRef}
            style={{ ...startStyles, ...transitionStyles[state] }}
            className={styles.segmentFill}
            data-qa-type="animated-progress"
          />
        )}
      </Transition>
    </div>
  );
};
